import { getAccessToken } from '@flash-tecnologia/hros-web-utility';
import { useState } from 'react';
import { Methods, ResponseError } from './api.types';

type UseBffFetchInput<bodyType, returnType> = {
  path: string;
  method?: Methods;
  body?: bodyType;
  onError?: (error: ResponseError) => void;
  onSuccess?: (data: returnType) => void;
};

interface UseBffFetchReturn<bodyType, returnType> {
  fetchBff: (body?: bodyType, method?: Methods) => Promise<void>;
  data: returnType | null;
  loading: boolean;
  error?: ResponseError;
  onError?: (error: ResponseError) => void;
  onSuccess?: (data: returnType) => void;
}

export const useBffFetch = <bodyType, returnType>({
  path,
  method = 'GET',
  body,
  onError,
  onSuccess,
}: UseBffFetchInput<bodyType, returnType>): UseBffFetchReturn<
  bodyType,
  returnType
> => {
  const [data, setData] = useState<returnType | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<ResponseError>();

  const fetchBff = async (fetchBody = body, fetchMethod = method) => {
    try {
      setLoading(true);
      const token = await getAccessToken();
      const response = await fetch(
        process.env.BFF_URL + '/v1/anti-fraud' + path,
        {
          headers: {
            'x-flash-auth': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          method: fetchMethod,
          body: JSON.stringify(fetchBody),
        },
      );
      if (!response.ok) {
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const errJson: ResponseError = await response.json();
          if (errJson.statusCode) {
            setError(errJson);
            onError && onError(errJson);
          } else {
            setError({ ...errJson, statusCode: response.status });
            onError && onError({ ...errJson, statusCode: response.status });
          }
          return;
        } else {
          const errJson = await response.text();
          setError({
            message: errJson,
            statusCode: response.status,
            error: errJson,
          });
          onError &&
            onError({
              message: errJson,
              statusCode: response.status,
              error: errJson,
            });
          return;
        }
      }
      const result: returnType = await response.json();
      setData(result);
      onSuccess && onSuccess(result);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      setError(err.message);
      onError && onError(err);
    } finally {
      setLoading(false);
    }
  };

  return {
    fetchBff,
    data,
    loading,
    error,
  };
};
